import { PickleballTrackerBrainService } from './../tracker-board/services/pickleball/pickleball-tracker-brain.service';
import { ServerService } from './../tracker-board/services/server.service';
import { ReturnerCourtSide } from './../../_shared/models/returnerSide.model';
import { Component, Input, OnDestroy, ViewChild } from '@angular/core';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { LoaderService } from '../../_core/services/loader.service';
import { StartMatchService } from './start-match.service';
import { Game, GameFormat } from '../../_shared/models/game.model';
import { Match, ScheduledItem } from '../../_shared/models/match.model';
import { Point, Server } from '../../_shared/models/point.model';
import { Set } from '../../_shared/models/set.model';
import { BoardHelpService } from '../tracker-board/board-help.service';
import { MatchService } from '../../_core/services/match.service';
import { SetType } from '../../_shared/models/score-format.model';
import { Score } from '../../_shared/models/csTypes/score';
import { Socket } from 'ngx-socket-io';
import { DisciplineEnum } from '../../_shared/models/csTypes/enums/discipline';
import { ServerPickleballOrder } from '../../_shared/models/serverPicklballOrder.model';
import { ClocksService } from '../../clocks/clocks.service';
import { User } from '../../_shared/models/user.model';
import { AuthService } from '../../auth/auth.service';
import { TrackerBoardComponent } from '../tracker-board/tracker-board.component';
import { BadmintonTrackerBrainService } from '../tracker-board/services/badminton/badminton-tracker-brain.service';
import { TableTennisTrackerBrainService } from '../tracker-board/services/table-tennis/table-tennis-tracker-brain.service';
import { SquashTrackerBrainService } from '../tracker-board/services/squash/squash-tracker-brain.service';

@Component({
  standalone: false,
  selector: 'app-start-match',
  templateUrl: './start-match.component.html',
  styleUrls: ['./start-match.component.scss'],
})
export class StartMatchComponent implements OnDestroy {
  @Input() match: Match;
  @Input() itemStyle = 'item';
  @ViewChild('board') trackerBoardComponent: TrackerBoardComponent | undefined;

  private readonly destroy$: Subject<void> = new Subject();

  constructor(
    private startMatchService: StartMatchService,
    private bhs: BoardHelpService,
    private loader: LoaderService,
    private matchService: MatchService,
    private socket: Socket,
    private serverService: ServerService,
    private pickleballTrackerBrainService: PickleballTrackerBrainService,
    private clocksService: ClocksService,
    private authService: AuthService,
    private badmintonTrackerBrainService: BadmintonTrackerBrainService,
    private tableTennisTrackerBrainService: TableTennisTrackerBrainService,
    private squashTrackerBrainService: SquashTrackerBrainService
  ) {}

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  async startMatch(isOfficial = false) {
    let serverTrackerMode = '';
    if (this.match.discipline !== DisciplineEnum.PICKLEBALL && 
      this.match.discipline !== DisciplineEnum.BADMINTON &&
      this.match.discipline !== DisciplineEnum.TABLE_TENNIS &&
      this.match.discipline !== DisciplineEnum.SQUASH) {
      serverTrackerMode = isOfficial
        ? 'individual'
        : await this.startMatchService.getServerTrackingMode(this.match);

      if (!serverTrackerMode) {
        return;
      }
    }

    let selectedServer: Server | null = null;
    let returnerCourtSides: ReturnerCourtSide[];
    let serverPickleballOrders: ServerPickleballOrder[] = [];
    let serverNumber: number;
    let side1PlayerPosition: any;
    let side2PlayerPosition: any;
    const scoreFormat = this.bhs.getScoreFormat(
      this.match.matchFormat,
      this.match.discipline
    );

    if (this.match.discipline === DisciplineEnum.PICKLEBALL) {
      if (
        scoreFormat.setType === SetType.STANDARD
      ) {
        serverNumber = this.match.matchType === 'D' ? 2: 1;
      }
      selectedServer = await this.pickleballTrackerBrainService.selectServer(
        this.match,
        true,
        serverNumber
      );
      side1PlayerPosition =
        this.pickleballTrackerBrainService.getPlayerPosition(1);
      side2PlayerPosition =
        this.pickleballTrackerBrainService.getPlayerPosition(2);
      if (!selectedServer) {
        return;
      }

      if (scoreFormat.setType === SetType.MLP) {
        serverPickleballOrders =
          this.pickleballTrackerBrainService.setServerOrder(
            this.match,
            selectedServer
          );
      }
    } else if (this.match.discipline === DisciplineEnum.BADMINTON) {
      // BADMINTON: At start of match (score 0-0), player 1 serves from right court (even score = 0)
      // For singles: always player 1
      // For doubles: player 1 serves first (score 0 is even)

      selectedServer = await this.badmintonTrackerBrainService.selectServer(
        this.match,
        true
      );
      side1PlayerPosition = this.badmintonTrackerBrainService.getPlayerPosition(1);
      side2PlayerPosition = this.badmintonTrackerBrainService.getPlayerPosition(2);
      if (!selectedServer) {
        return;
      }
    } else if (this.match.discipline === DisciplineEnum.TABLE_TENNIS) {
      const tableTennisServer = await this.tableTennisTrackerBrainService.selectServer(
        this.match,
        true
      );
      if (!tableTennisServer) {
        return;
      }
      selectedServer = tableTennisServer;
      side1PlayerPosition = this.tableTennisTrackerBrainService.getPlayerPosition(1);
      side2PlayerPosition = this.tableTennisTrackerBrainService.getPlayerPosition(2);
    } else if (this.match.discipline === DisciplineEnum.SQUASH) {
      // Squash: Similar to Badminton and Table Tennis, select server at start
      const squashServer = await this.squashTrackerBrainService.selectServer(
        this.match,
        true
      );
      if (!squashServer) {
        return;
      }
      selectedServer = squashServer;
      side1PlayerPosition = this.squashTrackerBrainService.getPlayerPosition(1);
      side2PlayerPosition = this.squashTrackerBrainService.getPlayerPosition(2);
    } else if (serverTrackerMode === 'individual') {
      selectedServer = await this.bhs.selectPlayer(this.match, true);
      if (!selectedServer) return;
    } else if (serverTrackerMode === 'side') {
      const sideNumber: number = await this.bhs.selectSide(this.match, true);

      if (!sideNumber) return;
      selectedServer = { sideNumber };
    }

    if (this.match.matchType === 'D' && serverTrackerMode === 'individual') {
      const returnSideNumber = selectedServer?.sideNumber === 1 ? 2 : 1;
      returnerCourtSides = await this.serverService.getReturnerCourtSides(
        this.match,
        returnSideNumber
      );
    }

    await this.loader.present();
    const authUser: User = await this.authService.getAuthUser();
    const playerCourtTimeLog = this.startMatchService.convertToPlayerCourtTimeLogFormat(this.match.sides, authUser?._id);
    const scheduledItemObj: ScheduledItem = {
      itemType: 'startTime',
      itemSubject: 'MATCHUP',
    };

    const point: Point = new Point();
    point.side1Score = 0;
    point.side2Score = 0;

    point.shots = [];
    point.pointNumber = 1;
    point.extensions?.push({
      name: 'areSidesReversed',
      value: false,
    });
    point.playerPositions = {
      side1: side1PlayerPosition,
      side2: side2PlayerPosition,
    };
    point.startPointTimeStamp = new Date();
    const onCourtPlayers = this.bhs.getOnCourtPlayersFromSides(this.match);
    // when start match is called from Intennse UI, we need to get the onCourtPlayers from the match 
    // and set only player id is set in the point.onCourtPlayers
    if (onCourtPlayers?.side1[0]?.player?.length > 0) {
      point.onCourtPlayers = onCourtPlayers;
    }    

    if (selectedServer) {
      selectedServer.returningSide = this.bhs.getReturningSide(1);
      point.server = selectedServer;
    }
    const {timeoutPerSet,remainingTimeoutsPerSet, timeoutPerMatch, remainingTimeoutsPerMatch} = this.clocksService.getTimeoutClockDetails(this.match)
    const defaultScoringType = (this.match.discipline === DisciplineEnum.PICKLEBALL || 
                                this.match.discipline === DisciplineEnum.BADMINTON || 
                                this.match.discipline === DisciplineEnum.TABLE_TENNIS || 
                                this.match.discipline === DisciplineEnum.SQUASH) ? 'pointByPoint' : 'gameByGame';
 
    const game = new Game();
    game.gameFormat =
      scoreFormat.setType === SetType.TIEBREAK
        ? GameFormat.TIEBREAK
        : GameFormat.STANDARD;
    game.points.push(point);
    game.gameNumber = 1;
    game.currentScoringType = defaultScoringType;
    const set:Set = new Set();
    set.returnerCourtSides = returnerCourtSides;
    set.serverPickleballOrders = serverPickleballOrders;
    set.games.push(game);
    set.setNumber = 1;
    set.previousScoringType = defaultScoringType;

    const score: Score = {
      scoreStringSide1: '0-0',
      scoreStringSide2: '0-0',
      side1PointScore: '0',
      side2PointScore: '0',
      server: selectedServer,
      sets: [
        {
          setNumber: 1,
          side1Score: 0,
          side2Score: 0,
          side1TiebreakScore: null,
          side2TiebreakScore: null,
          games: [],
          returnerCourtSides,
          serverPickleballOrders,
          timeout: timeoutPerSet > 0 ? {
            side1: {
              timeoutPerSet: timeoutPerSet, 
              remainingTimeoutsPerSet: remainingTimeoutsPerSet,
              timeoutPerMatch: timeoutPerMatch,
              remainingTimeoutsPerMatch: remainingTimeoutsPerMatch,
            }, side2: {
              timeoutPerSet: timeoutPerSet, 
              remainingTimeoutsPerSet: remainingTimeoutsPerSet,
              timeoutPerMatch: timeoutPerMatch,
              remainingTimeoutsPerMatch: remainingTimeoutsPerMatch,
            } 
          }: {}
        },
      ],
    };

    const notification = {
      title: 'Match has started',
      body: `${this.bhs.getPlayersNames(
        this.match,
        1
      )} - ${this.bhs.getPlayersNames(this.match, 2)}`,
    };

    this.startMatchService
      .startMatch(this.match._id, scheduledItemObj, [set], notification, score, playerCourtTimeLog)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.loader.dismiss())
      )
      .subscribe({
        next: (res) => {
          this.match.scheduledItems = res.data;
          this.match.sets = [set];
          this.match.score = score;
          this.match.matchStatus = 'IN_PROGRESS';
          this.match.startDate = new Date();
          this.match.playerCourtTimeLog = playerCourtTimeLog;
          this.startMatchService.publishStartMatch(this.match._id, true);
          this.matchService.publishScoreUpdated(this.match);
        },
      });
  }
}
